// Gross Profit = Revenue - COGS
export function grossProfit(revenue, cogs) {
  return revenue - cogs;
}

// Gross Profit % = Gross Profit / Revenue
export function grossProfitPct(revenue, cogs) {
  if (!revenue) return 0;
  return (revenue - cogs) / revenue;
}

// Operating Expense % = Expenses / Revenue
export function expensePct(revenue, expenses) {
  if (!revenue) return 0;
  return expenses / revenue;
}

// Net Profit % = Net Income / Revenue
export function netProfitPct(revenue, netIncome) {
  if (!revenue) return 0;
  return netIncome / revenue;
}

// Year-over-Year growth rate
export function yoyGrowth(currentRevenue, priorRevenue) {
  if (!priorRevenue) return null;
  return (currentRevenue - priorRevenue) / priorRevenue;
}

// Calculate the monthly NOI impact (12 months) for a single scenario
// Returns array of { revenue, cost, net } for each month (index 0 = Jan)
export function calcScenarioMonthly(scenario) {
  const months = Array.from({ length: 12 }, () => ({ revenue: 0, cost: 0, net: 0 }));

  switch (scenario.type) {
    case 'new-hire-support': {
      const monthlyCost = scenario.monthlySalary * (1 + scenario.payrollTaxRate);
      for (let m = scenario.startMonth - 1; m < 12; m++) {
        months[m].cost = -monthlyCost;
        months[m].net = -monthlyCost;
      }
      break;
    }
    case 'new-hire-revenue': {
      const monthlyCost = scenario.monthlySalary * (1 + scenario.payrollTaxRate);
      for (let m = scenario.startMonth - 1; m < 12; m++) {
        months[m].cost = -monthlyCost;
        months[m].net -= monthlyCost;
      }
      if (scenario.revenueStartMonth) {
        for (let m = scenario.revenueStartMonth - 1; m < 12; m++) {
          months[m].revenue = scenario.monthlyRevenue;
          months[m].net += scenario.monthlyRevenue;
        }
      }
      break;
    }
    case 'one-time-expense': {
      const m = scenario.month - 1;
      if (m >= 0 && m < 12) {
        months[m].cost = -scenario.amount;
        months[m].net = -scenario.amount;
      }
      break;
    }
    case 'recurring-expense': {
      for (let m = scenario.startMonth - 1; m < 12; m++) {
        months[m].cost = scenario.monthlyAmount;
        months[m].net = scenario.monthlyAmount;
      }
      break;
    }
    case 'revenue-change': {
      for (let m = scenario.startMonth - 1; m < 12; m++) {
        months[m].revenue = scenario.monthlyAmount;
        months[m].net = scenario.monthlyAmount;
      }
      break;
    }
    default:
      break;
  }
  return months;
}

// Compute cumulative monthly impact across all active scenarios
export function computeMonthlyImpact(scenarios) {
  const cumulative = Array.from({ length: 12 }, () => ({ revenue: 0, cost: 0, net: 0 }));
  scenarios
    .filter((s) => s.active)
    .forEach((scenario) => {
      const impact = calcScenarioMonthly(scenario);
      for (let m = 0; m < 12; m++) {
        cumulative[m].revenue += impact[m].revenue;
        cumulative[m].cost += impact[m].cost;
        cumulative[m].net += impact[m].net;
      }
    });
  return cumulative;
}

// Calculate forecast bands from historical YoY growth rates
export function calculateForecastBands(annualData) {
  const growthRates = [];
  for (let i = 1; i < annualData.length; i++) {
    const prior = annualData[i - 1].revenue;
    const current = annualData[i].revenue;
    if (prior && current && !annualData[i].projected) {
      growthRates.push((current - prior) / prior);
    }
  }

  if (growthRates.length === 0) return null;

  const conservative = Math.min(...growthRates);
  const aggressive = Math.max(...growthRates);
  const average = growthRates.reduce((a, b) => a + b, 0) / growthRates.length;

  // Use prior year (last non-projected) as base
  const lastActual = annualData.filter(d => !d.projected).slice(-1)[0];
  if (!lastActual) return null;

  const baseRevenue = lastActual.revenue;

  // Generate monthly forecast points (spread annual projection across 12 months)
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  return {
    conservative: months.map(m => ({
      month: m,
      value: Math.round(baseRevenue * (1 + conservative) * (m / 12)),
    })),
    average: months.map(m => ({
      month: m,
      value: Math.round(baseRevenue * (1 + average) * (m / 12)),
    })),
    aggressive: months.map(m => ({
      month: m,
      value: Math.round(baseRevenue * (1 + aggressive) * (m / 12)),
    })),
    rates: { conservative, average, aggressive },
  };
}
